import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { format, parseISO, isSameDay } from 'date-fns';
import { es } from 'date-fns/locale';
import { Plus, Clock, User, Mail, Trash2, Check, X, CalendarDays, Inbox } from 'lucide-react';
import AppointmentRequests from '@/components/AppointmentRequests';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const timeSlots = [
  '09:00', '10:00', '11:00', '12:00', '13:00', '14:00',
  '15:00', '16:00', '17:00', '18:00', '19:00', '20:00'
];

const Appointments = () => {
  const { user } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newAppointment, setNewAppointment] = useState({
    client_name: '',
    client_email: '',
    date: '',
    time: '',
    duration: 60,
    description: ''
  });

  useEffect(() => {
    fetchAppointments();
  }, []);

  const fetchAppointments = async () => {
    try {
      const response = await axios.get(`${API}/appointments`);
      setAppointments(response.data);
    } catch (error) {
      console.error('Error fetching appointments:', error);
      toast.error('Error al cargar las citas');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAppointment = async () => {
    if (!newAppointment.client_name || !newAppointment.date || !newAppointment.time) {
      toast.error('Completa los campos requeridos');
      return;
    }

    try {
      const response = await axios.post(`${API}/appointments`, {
        ...newAppointment,
        status: 'confirmed'
      });
      setAppointments(prev => [...prev, response.data]);
      setDialogOpen(false);
      setNewAppointment({
        client_name: '',
        client_email: '',
        date: '',
        time: '',
        duration: 60,
        description: ''
      });
      toast.success('Cita creada exitosamente');
    } catch (error) {
      console.error('Error creating appointment:', error);
      toast.error('Error al crear la cita');
    }
  };

  const handleUpdateStatus = async (appointmentId, status) => {
    try {
      await axios.put(`${API}/appointments/${appointmentId}`, { status });
      setAppointments(prev => prev.map(apt =>
        apt.id === appointmentId ? { ...apt, status } : apt
      ));
      toast.success(`Cita ${status === 'completed' ? 'completada' : 'cancelada'}`);
    } catch (error) {
      console.error('Error updating appointment:', error);
      toast.error('Error al actualizar la cita');
    }
  };

  const handleDeleteAppointment = async (appointmentId) => {
    if (!window.confirm('¿Seguro que quieres eliminar esta cita?')) return;
    try {
      await axios.delete(`${API}/appointments/${appointmentId}`);
      setAppointments(prev => prev.filter(apt => apt.id !== appointmentId));
      toast.success('Cita eliminada');
    } catch (error) {
      console.error('Error deleting appointment:', error);
      toast.error('Error al eliminar la cita');
    }
  };

  const appointmentsForSelectedDate = appointments.filter(apt =>
    apt.date === format(selectedDate, 'yyyy-MM-dd')
  );

  const datesWithAppointments = appointments.map(apt => parseISO(apt.date));

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'text-emerald-400';
      case 'completed': return 'text-zinc-500';
      case 'cancelled': return 'text-rose-400';
      default: return 'text-yellow-400';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'confirmed': return 'Confirmada';
      case 'completed': return 'Completada';
      case 'cancelled': return 'Cancelada';
      default: return 'Pendiente';
    }
  };

  // For clients - show their appointment requests
  if (user?.user_type === 'client') {
    return (
      <div className="max-w-4xl mx-auto px-3 sm:px-4 py-6 sm:py-8" data-testid="client-appointments">
        <div className="mb-8">
          <h1 className="font-['Cinzel'] text-3xl font-bold text-white">MIS CITAS</h1>
          <p className="text-zinc-500 mt-1">Tus solicitudes y citas programadas</p>
        </div>

        <Tabs defaultValue="requests" className="w-full">
          <TabsList className="bg-zinc-900 border border-zinc-800 w-full justify-start">
            <TabsTrigger value="requests" className="data-[state=active]:bg-zinc-800">
              <Inbox size={16} className="mr-2" />
              Solicitudes
            </TabsTrigger>
            <TabsTrigger value="confirmed" className="data-[state=active]:bg-zinc-800">
              <CalendarDays size={16} className="mr-2" />
              Citas Confirmadas
            </TabsTrigger>
          </TabsList>

          <TabsContent value="requests" className="mt-6">
            <AppointmentRequests />
          </TabsContent>

          <TabsContent value="confirmed" className="mt-6">
            {loading ? (
              <div className="space-y-4">
                {[...Array(2)].map((_, i) => (
                  <div key={i} className="h-24 bg-zinc-900 animate-pulse" />
                ))}
              </div>
            ) : appointments.length === 0 ? (
              <div className="text-center py-16 bg-zinc-900/30 border border-zinc-800">
                <CalendarDays className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                <p className="text-zinc-500">No tienes citas confirmadas</p>
              </div>
            ) : (
              <div className="space-y-4">
                {appointments.map((apt) => (
                  <div key={apt.id} className="bg-zinc-900/50 border border-zinc-800 p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-white">{apt.artist_name}</p>
                        <p className="text-sm text-zinc-500">
                          {format(parseISO(apt.date), "EEEE d 'de' MMMM", { locale: es })} a las {apt.time}
                        </p>
                        {apt.description && (
                          <p className="text-sm text-zinc-400 mt-2">{apt.description}</p>
                        )}
                      </div>
                      <span className={`text-xs uppercase tracking-wider ${getStatusColor(apt.status)}`}>
                        {getStatusLabel(apt.status)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-3 sm:px-4 py-6 sm:py-8" data-testid="appointments-container">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="font-['Cinzel'] text-3xl font-bold text-white">AGENDA</h1>
          <p className="text-zinc-500 mt-1">Gestiona tus citas y reservas</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-zinc-100 text-zinc-950 hover:bg-white uppercase tracking-widest text-xs font-bold"
              data-testid="new-appointment-btn"
            >
              <Plus size={16} className="mr-2" />
              Nueva Cita
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
            <DialogHeader>
              <DialogTitle className="font-['Cinzel'] text-xl">Nueva Cita</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Nombre del cliente *</Label>
                <Input
                  value={newAppointment.client_name}
                  onChange={(e) => setNewAppointment(prev => ({ ...prev, client_name: e.target.value }))}
                  className="bg-transparent border-zinc-800 mt-2"
                  placeholder="Nombre completo"
                  data-testid="appointment-client-name"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Email del cliente</Label>
                <Input
                  type="email"
                  value={newAppointment.client_email}
                  onChange={(e) => setNewAppointment(prev => ({ ...prev, client_email: e.target.value }))}
                  className="bg-transparent border-zinc-800 mt-2"
                  placeholder="cliente@email.com"
                  data-testid="appointment-client-email"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-500">Fecha *</Label>
                  <Input
                    type="date"
                    value={newAppointment.date}
                    onChange={(e) => setNewAppointment(prev => ({ ...prev, date: e.target.value }))}
                    className="bg-transparent border-zinc-800 mt-2"
                    data-testid="appointment-date"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-500">Hora *</Label>
                  <Select
                    value={newAppointment.time}
                    onValueChange={(value) => setNewAppointment(prev => ({ ...prev, time: value }))}
                  >
                    <SelectTrigger className="bg-transparent border-zinc-800 mt-2" data-testid="appointment-time">
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      {timeSlots.map((time) => (
                        <SelectItem key={time} value={time}>{time}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Duración (minutos)</Label>
                <Select
                  value={String(newAppointment.duration)}
                  onValueChange={(value) => setNewAppointment(prev => ({ ...prev, duration: parseInt(value) }))}
                >
                  <SelectTrigger className="bg-transparent border-zinc-800 mt-2" data-testid="appointment-duration">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="30">30 min</SelectItem>
                    <SelectItem value="60">1 hora</SelectItem>
                    <SelectItem value="90">1.5 horas</SelectItem>
                    <SelectItem value="120">2 horas</SelectItem>
                    <SelectItem value="180">3 horas</SelectItem>
                    <SelectItem value="240">4 horas</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Descripción</Label>
                <Textarea
                  value={newAppointment.description}
                  onChange={(e) => setNewAppointment(prev => ({ ...prev, description: e.target.value }))}
                  className="bg-transparent border-zinc-800 mt-2 min-h-[80px]"
                  placeholder="Detalles del tatuaje, referencias, etc."
                  data-testid="appointment-description"
                />
              </div>
              <Button
                onClick={handleCreateAppointment}
                className="w-full bg-zinc-100 text-zinc-950 hover:bg-white uppercase tracking-widest text-xs font-bold py-4"
                data-testid="save-appointment-btn"
              >
                Crear Cita
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Calendar */}
        <div className="lg:col-span-1">
          <div className="bg-zinc-900/50 border border-zinc-800 p-6" data-testid="calendar-container">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              locale={es}
              modifiers={{
                hasAppointment: datesWithAppointments
              }}
              modifiersStyles={{
                hasAppointment: {
                  position: 'relative'
                }
              }}
              className="w-full"
            />
          </div>
        </div>

        {/* Appointments List */}
        <div className="lg:col-span-2">
          <div className="mb-4">
            <h2 className="font-['Cinzel'] text-xl font-semibold text-white">
              {format(selectedDate, "EEEE, d 'de' MMMM", { locale: es })}
            </h2>
            <p className="text-sm text-zinc-500">
              {appointmentsForSelectedDate.length} cita{appointmentsForSelectedDate.length !== 1 ? 's' : ''}
            </p>
          </div>

          {loading ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-32 bg-zinc-900 animate-pulse" />
              ))}
            </div>
          ) : appointmentsForSelectedDate.length === 0 ? (
            <div className="text-center py-16 bg-zinc-900/30 border border-zinc-800">
              <p className="text-zinc-500">No hay citas para este día</p>
            </div>
          ) : (
            <div className="space-y-4">
              {appointmentsForSelectedDate
                .sort((a, b) => a.time.localeCompare(b.time))
                .map((appointment) => (
                  <div
                    key={appointment.id}
                    className="bg-zinc-900/50 border border-zinc-800 p-6"
                    data-testid={`appointment-${appointment.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-4 mb-3">
                          <span className="font-mono text-2xl text-white">{appointment.time}</span>
                          <span className={`text-xs uppercase tracking-wider ${getStatusColor(appointment.status)}`}>
                            {getStatusLabel(appointment.status)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-zinc-400 mb-1">
                          <User size={14} />
                          <span>{appointment.client_name}</span>
                        </div>
                        {appointment.client_email && (
                          <div className="flex items-center gap-2 text-zinc-500 text-sm">
                            <Mail size={14} />
                            <span>{appointment.client_email}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2 text-zinc-500 text-sm mt-1">
                          <Clock size={14} />
                          <span>{appointment.duration} min</span>
                        </div>
                        {appointment.description && (
                          <p className="text-zinc-500 text-sm mt-3 pt-3 border-t border-zinc-800">
                            {appointment.description}
                          </p>
                        )}
                      </div>

                      {appointment.status !== 'completed' && appointment.status !== 'cancelled' && (
                        <div className="flex gap-2">
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleUpdateStatus(appointment.id, 'completed')}
                            className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-900/20"
                            title="Marcar como completada"
                            data-testid={`complete-${appointment.id}`}
                          >
                            <Check size={18} />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleUpdateStatus(appointment.id, 'cancelled')}
                            className="text-rose-400 hover:text-rose-300 hover:bg-rose-900/20"
                            title="Cancelar cita"
                            data-testid={`cancel-${appointment.id}`}
                          >
                            <X size={18} />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleDeleteAppointment(appointment.id)}
                            className="text-zinc-500 hover:text-zinc-300"
                            title="Eliminar cita"
                            data-testid={`delete-${appointment.id}`}
                          >
                            <Trash2 size={18} />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>

      {/* Appointment Requests Section for Artists */}
      <div className="mt-12 pt-8 border-t border-zinc-800">
        <AppointmentRequests />
      </div>
    </div>
  );
};

export default Appointments;
